/**
 * React Hooks for Sound Management
 * 
 * Provides easy-to-use hooks for sound integration in React components
 * with proper memoization and cleanup
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SoundId, SoundManagerState } from '../../core/services/managers/SoundManager';
import { soundManager } from '../../core/services/managers/SoundManager';
import { useAssetsConfig } from '../../store/app/appStore';
import { features } from '../../core/config/features';

/**
 * Hook for managing the sound manager lifecycle
 * Handles initialization and provides access to the sound manager state
 */
export const useSoundManager = () => {
  const [state, setState] = useState<SoundManagerState>(soundManager.getState());
  const assetsConfig = useAssetsConfig();
  const initializationRef = useRef<Promise<void> | null>(null);
  const lastInitializedHashRef = useRef<string | undefined>(undefined);

  // Initialize sound manager when assets config is available
  useEffect(() => {
    if (!features.enableSoundEffects) {
      return;
    }

    // For CDN hosts, wait for the real assetsHash before initializing so we don't
    // fire a doomed request without the hash in the path (it resolves asynchronously).
    const isCdnHost = !!assetsConfig.assetsHost && !assetsConfig.assetsHost.includes('localhost');
    const isWaitingForHash = isCdnHost && !assetsConfig.assetsHash;

    // Initialize if we have assetsHost and either:
    // 1. We haven't initialized yet (!initializationRef.current)
    // 2. The hash has changed from empty to a real value
    const shouldInitialize = assetsConfig.assetsHost &&
      !isWaitingForHash &&
      (!initializationRef.current ||
        (lastInitializedHashRef.current === '' && assetsConfig.assetsHash !== ''));

    if (shouldInitialize) {
      // Reset the sound manager if re-initializing with new hash
      if (initializationRef.current && lastInitializedHashRef.current === '' && assetsConfig.assetsHash !== '') {
        soundManager.reset();
      }

      lastInitializedHashRef.current = assetsConfig.assetsHash;
      initializationRef.current = soundManager
        .initialize(assetsConfig.assetsHost, assetsConfig.assetsHash || '')
        .then(() => {
          setState(soundManager.getState());
        })
        .catch((error) => {
          console.error('[useSoundManager] Initialization failed:', error);
          setState(soundManager.getState());
        });
    }
  }, [assetsConfig.assetsHost, assetsConfig.assetsHash]);

  // Subscribe to sound manager state changes
  useEffect(() => {
    // Subscribe to state changes via event system
    const unsubscribe = soundManager.subscribeToStateChanges((newState) => {
      setState(newState);
    });
    
    return () => {
      unsubscribe();
    };
  }, []);

  const playSound = useCallback((soundId: SoundId) => {
    soundManager.playSound(soundId);
  }, []);

  const setVolume = useCallback((volume: number) => {
    soundManager.setVolume(volume);
  }, []);

  const getSoundDuration = useCallback((soundId: SoundId) => {
    return soundManager.getSoundDuration(soundId);
  }, []);

  return {
    state,
    playSound,
    setVolume,
    getSoundDuration,
    isReady: state.isInitialized && !state.hasError,
    isLoading: state.isLoading,
    hasError: state.hasError,
    errorMessage: state.errorMessage,
  };
};

/**
 * Hook for playing individual sounds with proper cleanup
 * Returns a memoized play function for a specific sound
 */
// fallow-ignore-next-line unused-export
export const useSound = (soundId: SoundId) => {
  const playSound = useCallback(() => {
    if (features.enableSoundEffects) {
      soundManager.playSound(soundId);
    }
  }, [soundId]);

  const getDuration = useCallback(() => {
    return soundManager.getSoundDuration(soundId);
  }, [soundId]);

  return {
    play: playSound,
    duration: getDuration(),
  };
};

/**
 * Hook for game-specific sound interactions
 * Provides all game-related sound functions
 */
export const useGameSounds = () => {
  const playHover = useCallback(() => {
    soundManager.playHoverSound();
  }, []);

  const playSelect = useCallback(() => {
    soundManager.playSelectGameSound();
  }, []);

  const playTab = useCallback(() => {
    soundManager.playSelectTabSound();
  }, []);

  const playExit = useCallback(() => {
    soundManager.playExitAppSound();
  }, []);

  // Get exit sound duration for timing purposes
  const getExitDuration = useCallback(() => {
    return soundManager.getSoundDuration('exitAppSound');
  }, []);

  return {
    playHoverSound: playHover,
    playSelectGameSound: playSelect,
    playSelectTabSound: playTab,
    playExitAppSound: playExit,
    getExitSoundDuration: getExitDuration,
  };
};

/**
 * Hook for hover sound with automatic cleanup
 * Designed for use with onMouseEnter/onMouseLeave events
 */
// fallow-ignore-next-line unused-export
export const useHoverSound = () => {
  const playHoverSound = useCallback(() => {
    soundManager.playHoverSound();
  }, []);

  return playHoverSound;
};

/**
 * Hook for tracking sound events for analytics
 * Provides functions that both play sounds and track events
 */
// fallow-ignore-next-line unused-export
export const useSoundAnalytics = () => {
  const playWithAnalytics = useCallback((soundId: SoundId, _context?: string) => {
    soundManager.playSound(soundId);
    
    // Could integrate with analytics store here
  }, []);

  const playHoverWithAnalytics = useCallback((_gameId?: string) => {
    soundManager.playHoverSound();
    
  }, []);

  const playSelectWithAnalytics = useCallback((_gameId?: string) => {
    soundManager.playSelectGameSound();
    
  }, []);

  return {
    playWithAnalytics,
    playHoverWithAnalytics,
    playSelectWithAnalytics,
  };
};

/**
 * Hook for sound volume control
 * Provides volume management functionality
 */
// fallow-ignore-next-line unused-export
export const useSoundVolume = () => {
  const [volume, setVolumeState] = useState(soundManager.getVolume());

  const setVolume = useCallback((newVolume: number) => {
    soundManager.setVolume(newVolume);
    setVolumeState(newVolume);
  }, []);

  const getVolume = useCallback(() => {
    return soundManager.getVolume();
  }, []);

  return {
    volume,
    setVolume,
    getVolume,
  };
};

/**
 * Hook for sound debugging
 * Provides debugging information about the sound system
 */
// fallow-ignore-next-line unused-export
export const useSoundDebug = () => {
  const [debugInfo, setDebugInfo] = useState(() => soundManager.getState());

  useEffect(() => {
    const interval = setInterval(() => {
      setDebugInfo(soundManager.getState());
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const triggerTest = useCallback((soundId: SoundId) => {
    soundManager.playSound(soundId);
  }, []);

  return {
    debugInfo,
    triggerTest,
    soundManager, // Direct access for advanced debugging
  };
};
